import { useCallback, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLanguageCode } from '@project-types/AppLanguageCodes';
import { Button } from 'react-aria-components';
import { invalidFormValue } from '@components/utils';
import { DispatchPoint } from '@api/types/DispatchPoint';
import { convertPplToDispatchPoint } from '@pages/order/payment-shipping/ppl-script/convertors';
import { isPplParcelshopEvent } from '@pages/order/payment-shipping/ppl-script/typescriptguards';
import { useScript } from '@hooks/use-script/useScript';
import { FORM_SELECT_LANGUAGES, DEFAULT_LANGUAGE } from '@@/languageConfig';



type PplScriptProps = {
	dispatchPoint: DispatchPoint | null;
	showInvalids: boolean;
	country: AppLanguageCode;
	updateDispatchPoint: (value: DispatchPoint | null) => void;
}


export const PplScript = (props: PplScriptProps) => {
	const { dispatchPoint, showInvalids, country,updateDispatchPoint } = props;
	const { t } = useTranslation('forms');

	const dialogRef = useRef<HTMLDialogElement>(null);

	const [ isDialogOpen, setIsDialogOpen ] = useState(false);
	useScript('https://www.ppl.cz/sources/map/main.js');

	const handleSelectDispatchPoint = (event: Event) => {
		if (isPplParcelshopEvent(event)) {
			const convertedObject = convertPplToDispatchPoint(event.detail);
			if (!convertedObject) return;

			updateDispatchPoint(convertedObject);
			closeDialog();
		}
	};
	
	
	useEffect(() => {
		document.addEventListener('ppl-parcelshop-map', handleSelectDispatchPoint);
		return () => {
			document.removeEventListener('ppl-parcelshop-map', handleSelectDispatchPoint);
		}
	}, []);


	useEffect(() => {
		const link = document.createElement('link');
		link.href = 'https://www.ppl.cz/sources/map/main.css';
		link.rel = 'stylesheet';
		document.head.appendChild(link);

		return () => {
			document.head.removeChild(link);
		}
	}, []);

	
	useEffect(() => {
		if (isDialogOpen) {
			window.dispatchEvent(new Event('resize'));
		}
	}, [ isDialogOpen ]);
	

	const openDialog = useCallback(() => {
		if (dialogRef && dialogRef.current) {
			dialogRef.current.showModal();
			setIsDialogOpen(true);
		}
	},[ dialogRef ]);

	
	const closeDialog = useCallback(() => {
		if (dialogRef && dialogRef.current) {
			dialogRef.current.close();
			setIsDialogOpen(false);
		}
	},[ dialogRef ]);


	return (
		<>
			<label className='dispatch-point-label'>
				{t('paymentShipping.shipping.dispatchPointLabel') + ': '}<span>*</span> {showInvalids && invalidFormValue(dispatchPoint) &&
					<p className='form-invalid'>{t('validation.requiredField')}</p>}
			</label>

			<div className='dispatch-point-text'>
				
				<p>{dispatchPoint ? `${dispatchPoint.name}, ${dispatchPoint.street}, ${dispatchPoint.city}` : t('paymentShipping.shipping.notSelect')}</p>

				<Button className='app-form-button disable-selection' autoFocus={false} onPress={openDialog}>
					{dispatchPoint ? t('shared.change') : t('shared.select')}
				</Button>

			</div>

			<dialog
				className='ppl-dispatch-modal'
				ref={dialogRef}
			 	open={isDialogOpen} 
			 	onClose={closeDialog}>
					<div
						id="ppl-parcelshop-map"
						data-datamode="default" 
						data-country={country.toLowerCase()}
						data-countries={FORM_SELECT_LANGUAGES && FORM_SELECT_LANGUAGES.length > 0
							? FORM_SELECT_LANGUAGES.join(',').toLowerCase()
							: DEFAULT_LANGUAGE.toLowerCase()
						}
					></div>
					<Button className='disable-selection modal-close-button' autoFocus={false} onPress={closeDialog}>
						{t('shared.close')}
					</Button>
			</dialog>
		</>
	);
};